import React, { useState, useEffect } from "react";
import { useParams, useHistory } from "react-router-dom";

import { toast } from "react-toastify";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { 
  Paper, 
  makeStyles, 
  Grid, 
  IconButton,
  Tooltip,
  Fab,
  Zoom,
  Badge,
  useMediaQuery,
  useTheme
} from "@material-ui/core";
import {
  ViewWeek as ViewKanbanIcon,
  Close as CloseIcon,
  ArrowLeft as ArrowLeftIcon,
  ArrowRight as ArrowRightIcon
} from "@material-ui/icons";

import ContactDrawer from "../ContactDrawer";
import MessageInput from "../MessageInput/";
import TicketHeader from "../TicketHeader";
import TicketInfo from "../TicketInfo";
import TicketActionButtons from "../TicketActionButtons";
import MessagesList from "../MessagesList";
import TicketKanbanPanel from "../TicketKanbanPanel";
import useTicketCard from "../../hooks/useTicketCard";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import { ReplyMessageProvider } from "../../context/ReplyingMessage/ReplyingMessageContext";
import toastError from "../../errors/toastError";

const drawerWidth = 320;
const kanbanPanelWidth = 280;

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100%",
    position: "relative",
    overflow: "hidden",
  },

  ticketInfo: {
    maxWidth: "50%",
    flexBasis: "50%",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "80%",
      flexBasis: "80%",
    },
  },
  ticketActionButtons: {
    maxWidth: "50%",
    flexBasis: "50%",
    display: "flex",
    [theme.breakpoints.down("sm")]: {
      maxWidth: "100%",
      flexBasis: "100%",
      marginBottom: "5px",
    },
  },

  mainWrapper: {
    flex: 1,
    height: "100%",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderTopLeftRadius: 0,
    borderBottomLeftRadius: 0,
    borderLeft: "0",
    marginRight: -drawerWidth,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },

  mainWrapperShift: {
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginRight: 0,
  },
  
  messagesListWrapper: {
    overflow: "hidden",
    flexGrow: 1,
    display: "flex",
    position: "relative",
  },
  
  messagesList: {
    flexGrow: 1,
    minHeight: 600,
    overflowY: "scroll",
    //padding: theme.spacing(1),
    transition: theme.transitions.create("width", {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    width: "100%",
  },
  
  messagesListWithPanel: {
    [theme.breakpoints.up("md")]: {
      width: `calc(100% - ${kanbanPanelWidth}px)`,
    },
  },
  
  kanbanPanel: {
    width: kanbanPanelWidth,
    position: "relative",
    transition: theme.transitions.create(["width", "transform"], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.enteringScreen,
    }),
    borderLeft: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down("sm")]: {
      position: "absolute",
      right: 0,
      top: 0,
      bottom: 0,
      zIndex: 10,
      transform: "translateX(100%)",
      width: "85%",
      maxWidth: 320,
      boxShadow: theme.shadows[5],
    },
    [theme.breakpoints.up("md")]: {
      transform: "translateX(0)",
      opacity: 1,
    },
  },
  
  kanbanPanelHidden: {
    [theme.breakpoints.up("md")]: {
      width: 0,
      opacity: 0,
      overflow: "hidden",
    },
  },
  
  kanbanPanelVisible: {
    [theme.breakpoints.down("sm")]: {
      transform: "translateX(0)",
    },
  },
  
  kanbanToggleButton: {
    position: "absolute",
    bottom: theme.spacing(3),
    right: theme.spacing(3),
    zIndex: 20,
  },
  
  closePanelButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
    zIndex: 20,
  },
  
  kanbanPanelCollapseButton: {
    position: "absolute",
    top: "50%",
    left: -16,
    zIndex: 20,
    backgroundColor: theme.palette.background.paper,
    border: `1px solid ${theme.palette.divider}`,
    transform: "translateY(-50%)",
    "& .MuiSvgIcon-root": {
      fontSize: "1rem",
    },
  },
}));

const Ticket = () => {
  const { ticketId } = useParams();
  const history = useHistory();
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { card } = useTicketCard(ticketId);

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [kanbanPanelOpen, setKanbanPanelOpen] = useState(false); // Sempre inicia fechado
  const [loading, setLoading] = useState(true);
  const [contact, setContact] = useState({});
  const [ticket, setTicket] = useState({});

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchTicket = async () => {
        try {
          const { data } = await api.get("/tickets/" + ticketId);

          setContact(data.contact);
          setTicket(data);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchTicket();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [ticketId, history]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("connect", () => socket.emit("joinChatBox", ticketId));

    socket.on("ticket", (data) => {
      if (data.action === "update") {
        setTicket(data.ticket);
      }

      if (data.action === "delete") {
        toast.success("Ticket deleted sucessfully.");
        history.push("/tickets");
      }
    });

    socket.on("contact", (data) => {
      if (data.action === "update") {
        setContact((prevState) => {
          if (prevState.id === data.contact?.id) {
            return { ...prevState, ...data.contact };
          }
          return prevState;
        });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, [ticketId, history]);

  // Adiciona efeito para fechar o painel kanban em dispositivos móveis quando mudar para modo mobile
  useEffect(() => {
    if (isMobile) {
      setKanbanPanelOpen(false);
    }
  }, [isMobile]);

  const handleDrawerOpen = () => {
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
  };
  
  const toggleKanbanPanel = () => {
    setKanbanPanelOpen(!kanbanPanelOpen);
  };

  return (
    <div className={classes.root} id="drawer-container">
      <Paper
        variant="outlined"
        elevation={0}
        className={clsx(classes.mainWrapper, {
          [classes.mainWrapperShift]: drawerOpen,
        })}
      >
        <TicketHeader loading={loading}>
          <div className={classes.ticketInfo}>
            <TicketInfo
              contact={contact}
              ticket={ticket}
              onClick={handleDrawerOpen}
            />
          </div>
          <div className={classes.ticketActionButtons}>
            <TicketActionButtons 
              ticket={ticket} 
              toggleKanbanPanel={toggleKanbanPanel}
              kanbanPanelOpen={kanbanPanelOpen}
              hasCard={card ? true : false}
            />
          </div>
        </TicketHeader>
        <ReplyMessageProvider>
          <div className={classes.messagesListWrapper}>
            <div className={clsx(
              classes.messagesList, 
              { [classes.messagesListWithPanel]: kanbanPanelOpen }
            )}>
              <MessagesList
                ticketId={ticketId}
                isGroup={ticket.isGroup}
                isPanelOpen={kanbanPanelOpen}
              />
            </div>
            
            <div className={clsx(
              classes.kanbanPanel,
              { 
                [classes.kanbanPanelVisible]: kanbanPanelOpen,
                [classes.kanbanPanelHidden]: !kanbanPanelOpen
              }
            )}>
              {isMobile && (
                <IconButton 
                  className={classes.closePanelButton}
                  onClick={toggleKanbanPanel}
                  size="small"
                >
                  <CloseIcon />
                </IconButton>
              )}
              
              {!isMobile && (
                <IconButton 
                  className={classes.kanbanPanelCollapseButton}
                  onClick={toggleKanbanPanel}
                  size="small"
                >
                  {kanbanPanelOpen ? <ArrowRightIcon /> : <ArrowLeftIcon />}
                </IconButton>
              )}
              
              <TicketKanbanPanel 
                ticketId={ticketId} 
                ticketStatus={ticket.status}
                queueId={ticket.queueId}
              />
            </div>
          </div>
          
          <MessageInput ticketStatus={ticket.status} />
        </ReplyMessageProvider>
      </Paper>
      <ContactDrawer
        open={drawerOpen}
        handleDrawerClose={handleDrawerClose}
        contact={contact}
        loading={loading}
      />
    </div>
  );
};

export default Ticket;
